import {ReactNode, useState} from 'react';
import {useDisclosure} from '@chakra-ui/react';
import {Archive, Download, FilePen, FolderInput, Star, Trash} from 'lucide-react';
import {toaster} from '../../components/ui/toaster';
import {DriveFile} from '../../drive/types';
import {loadImage} from '../../imageCache/blobCache';
import {ConfirmDeleteDialog} from '../../components/ConfirmDeleteDialog';
import {ConfirmArchiveDialog} from '../../components/ConfirmArchiveDialog';
import {MoveFileModal, MoveFileTrailItem} from '../../components/MoveFileModal';
import {RenameModal} from '../../components/RenameModal';
import {ContextMenuControl, FileContextMenu, plazmaStarAction} from '../../components/FileContextMenu';
import {useDeleteFiles, useDriveCall, useRenameFile} from '../../drive/queries';
import {describeDriveError, getFileMediaBlob} from '../../drive/driveApi';
import {PlazmaStars} from '../../hooks/usePlazmaStars';
import {canDeleteFile, DELETE_FORBIDDEN_MESSAGE} from '../../domain/permissions';
import {useArchiveWithUndo} from '../../hooks/useArchiveWithUndo';
import {copyFileName} from '../../components/copyFileName';
import {downloadBlob} from '../../utils/downloadBlob';

/**
 * One thing the viewer can do with its photo. The top bar shows it as a button and the keyboard
 * runs it by its `keys`; adding a feature to the viewer is adding one of these.
 */
export interface ViewerAction {
    id: string;
    /** The accessible name, and the menu item's text. */
    label: string;
    /** The hover text; defaults to the label. */
    title?: string;
    icon?: ReactNode;
    /** Shown next to the icon from `md` up. */
    text?: string;
    /** `KeyboardEvent.key` values (letters in lower case) that run it, without Ctrl, ⌘ or Alt. */
    keys?: string[];
    /**
     * Where the top bar shows it: always, only from `md` up (on phones it is in the "⋯" menu), or
     * never (keyboard only).
     */
    bar: 'always' | 'desktop' | 'never';
    disabled?: boolean;
    pending?: boolean;
    /** A toggle's state (the plazma star). */
    pressed?: boolean;
    color?: string;
    /** Run by the button and the shortcut alike, also while disabled: it explains itself (e.g. with a toast). */
    run: () => void;
}

interface ViewerActionsInput {
    current: DriveFile;
    parentFolderId: string;
    sourceDriveId: string;
    initialTrail: MoveFileTrailItem[];
    /** Set in the web bin. */
    archiveFolderId?: string;
    plazma?: PlazmaStars;
    contextMenu: ContextMenuControl;
    /** Steps away from the photo once it has left the listing (archived, moved, deleted). */
    onRemoved: () => void;
}

export interface ViewerActions {
    /** In the top bar's order. */
    actions: ViewerAction[];
    /** Whether the photo can be archived (the web bin): offered by the bar, the `x` key and a swipe up. */
    canArchive: boolean;
    requestMoveToArchive: () => void;
    /** The right-click (and "⋯") menu: belongs inside the viewer's dialog content, within its focus trap. */
    contextMenuElement: ReactNode;
    /** The confirmation, move and rename dialogs. */
    dialogs: ReactNode;
    /** One of the dialogs or the context menu is open: the viewer's shortcuts wait. */
    isBusy: boolean;
}

/** The viewer's actions on its current photo, with the dialogs they open. */
export function useViewerActions({
    current,
    parentFolderId,
    sourceDriveId,
    initialTrail,
    archiveFolderId,
    plazma,
    contextMenu,
    onRemoved,
}: ViewerActionsInput): ViewerActions {
    const callDrive = useDriveCall();
    const [isDownloading, setIsDownloading] = useState(false);
    const archiveWithUndo = useArchiveWithUndo();
    const deleteFiles = useDeleteFiles();
    const renameFile = useRenameFile();
    const deleteDialog = useDisclosure();
    const archiveDialog = useDisclosure();
    const moveModal = useDisclosure();
    const renameModal = useDisclosure();

    const isStarred = !!plazma?.isStarred(current);
    // Drive's capabilities.canDelete: the button is disabled, the shortcut answers with a toast.
    const canDeleteCurrent = canDeleteFile(current);
    const plazmaCopiesForbidden = isStarred && !!plazma && !plazma.canRemoveCopies([current]);
    const canArchive = !!archiveFolderId;

    function requestDelete() {
        if (!canDeleteCurrent) {
            toaster.create({type: 'error', title: DELETE_FORBIDDEN_MESSAGE});
            return;
        }
        deleteDialog.onOpen();
    }

    async function handleDownload() {
        // The top bar's button shows it's busy; the context menu can still ask again meanwhile.
        if (isDownloading) return;
        setIsDownloading(true);
        try {
            const entry = await loadImage(current.id, () => callDrive((token) => getFileMediaBlob(token, current.id)));
            downloadBlob(entry.blob, current.name);
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        } finally {
            setIsDownloading(false);
        }
    }

    function requestMoveToArchive() {
        // Archiving is a web-bin action only (the button, the `x` key and the swipe alike).
        if (!archiveFolderId || archiveWithUndo.isArchiving) return;
        if (isStarred) archiveDialog.onOpen();
        else handleMoveToArchive(false);
    }

    async function handleMoveToArchive(alsoRemovePlazmaCopy: boolean) {
        if (!archiveFolderId) return;
        // No confirmation for a fast workflow: the success toast offers an undo instead.
        const archived = await archiveWithUndo.archive({
            files: [current],
            webFolderId: parentFolderId,
            archiveFolderId,
            plazma,
            alsoRemovePlazmaCopies: alsoRemovePlazmaCopy && !plazmaCopiesForbidden,
        });
        archiveDialog.onClose();
        if (archived) onRemoved();
    }

    async function handleDelete(alsoRemovePlazmaCopy: boolean) {
        if (!canDeleteCurrent) return;
        const plazmaCopies = alsoRemovePlazmaCopy && !plazmaCopiesForbidden && plazma ? plazma.copiesOf([current]) : [];
        try {
            await deleteFiles.mutateAsync({fileIds: [current.id], parentId: parentFolderId});
            await plazma?.removeCopies(plazmaCopies);
            toaster.create({type: 'success', title: 'Fájl törölve'});
            deleteDialog.onClose();
            onRemoved();
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
            deleteDialog.onClose();
        }
    }

    async function handleRename(newName: string, alsoRenamePlazmaCopy: boolean) {
        const plazmaCopies = alsoRenamePlazmaCopy && plazma ? plazma.copiesOf([current]) : [];
        try {
            await renameFile.mutateAsync({fileId: current.id, newName, parentId: parentFolderId});
            await plazma?.renameCopies(plazmaCopies, newName);
            toaster.create({type: 'success', title: 'Átnevezve'});
            renameModal.onClose();
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        }
    }

    const actions: ViewerAction[] = [];
    if (plazma) {
        actions.push({
            id: 'star',
            label: isStarred ? 'Eltávolítás a plazmából' : 'Plazmába másolás',
            title: isStarred ? 'Plazmában — kattints az eltávolításhoz (S)' : 'Plazmába másolás (S)',
            icon: <Star size={18} fill={isStarred ? 'currentColor' : 'none'}/>,
            keys: ['s'],
            bar: 'always',
            pressed: isStarred,
            color: isStarred ? 'yellow.300' : undefined,
            pending: plazma.isPending(current),
            disabled: !plazma.canToggle(current),
            run: () => plazma.toggle(current),
        });
    }
    actions.push({
        id: 'download',
        label: 'Letöltés',
        icon: <Download size={18}/>,
        bar: 'desktop',
        pending: isDownloading,
        run: handleDownload,
    });
    if (canArchive) {
        actions.push({
            id: 'archive',
            label: 'Archívumba',
            title: 'Archívumba (X)',
            icon: <Archive size={18}/>,
            text: 'Archívumba',
            keys: ['x'],
            bar: 'always',
            pending: archiveWithUndo.isArchiving,
            run: requestMoveToArchive,
        });
    }
    actions.push(
        {id: 'move', label: 'Áthelyezés', icon: <FolderInput size={18}/>, bar: 'desktop', run: moveModal.onOpen},
        {id: 'rename', label: 'Átnevezés', icon: <FilePen size={18}/>, bar: 'desktop', run: renameModal.onOpen},
        {
            id: 'delete',
            label: 'Törlés',
            title: canDeleteCurrent ? 'Törlés' : DELETE_FORBIDDEN_MESSAGE,
            icon: <Trash size={18}/>,
            keys: ['delete', 'backspace'],
            bar: 'desktop',
            disabled: !canDeleteCurrent,
            run: requestDelete,
        },
        {id: 'copy-name', label: 'Fájlnév másolása', keys: ['n'], bar: 'never', run: () => copyFileName(current.name)},
    );

    const contextMenuElement = <FileContextMenu
        menu={contextMenu}
        star={plazma && plazmaStarAction(plazma, [current])}
        onDownload={handleDownload}
        onMoveToArchive={canArchive ? requestMoveToArchive : undefined}
        onMove={moveModal.onOpen}
        onRename={renameModal.onOpen}
        onDelete={requestDelete}
        deleteDisabledReason={canDeleteCurrent ? undefined : DELETE_FORBIDDEN_MESSAGE}
    />;
    const dialogs = <>
        <ConfirmDeleteDialog
            isOpen={deleteDialog.open}
            onClose={deleteDialog.onClose}
            onConfirm={handleDelete}
            count={1}
            isDeleting={deleteFiles.isPending}
            plazmaStarredCount={isStarred ? 1 : 0}
            plazmaCopiesForbidden={plazmaCopiesForbidden}
        />
        <ConfirmArchiveDialog
            isOpen={archiveDialog.open}
            onClose={archiveDialog.onClose}
            onConfirm={handleMoveToArchive}
            count={1}
            plazmaStarredCount={1}
            plazmaCopiesForbidden={plazmaCopiesForbidden}
            isArchiving={archiveWithUndo.isArchiving}
        />
        <MoveFileModal
            isOpen={moveModal.open}
            onClose={moveModal.onClose}
            files={[current]}
            sourceFolderId={parentFolderId}
            sourceDriveId={sourceDriveId}
            initialTrail={initialTrail}
            onDone={onRemoved}
            plazmaStarredFiles={isStarred ? [current] : []}
            plazma={plazma}
        />
        <RenameModal
            isOpen={renameModal.open}
            onClose={renameModal.onClose}
            currentName={current.name}
            onSubmit={handleRename}
            plazmaStarred={isStarred}
            isSubmitting={renameFile.isPending}
        />
    </>;

    return {
        actions,
        canArchive,
        requestMoveToArchive,
        contextMenuElement,
        dialogs,
        isBusy: deleteDialog.open || archiveDialog.open || moveModal.open || renameModal.open || contextMenu.isOpen,
    };
}
